import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import PlayControl from '../PlayControl/PlayControl';
import VibeTooltip from '../VibeTooltip/VibeTooltip';
import {
  getDayOfWeekIndex,
} from '../../utils/TimeUtil';
import {
  isMessageApplicable,
} from '../../helpers/Location';
import * as color from '../../sass/color.scss';
import './MessageListItem.scss';
import VibeIcon from '../VibeIcon/VibeIcon';
import viPlayCircleOutline from '../../icons/viPlayCircleOutline';
import viCalendar from '../../icons/viCalendar';

class MessageListItem extends PureComponent {
  constructor(props) {
    super(props);

    const {
      companyId,
      locationId,
      startDate,
      stopDate,
      locations = [],
      messageBlockLocations = [],
    } = props;

    let startDateFormatted = null;
    let stopDateFormatted = null;

    const willPlay = isMessageApplicable({
      companyId,
      locationId,
      locations,
      parentLocations: messageBlockLocations,
    });

    if (startDate || stopDate) {
      const startAt = startDate
        ? new moment(startDate).startOf('day')
        : null;
      const stopAt = stopDate
        ? new moment(stopDate).endOf('day')
        : null;

      if (startAt) {
        startDateFormatted = startAt.format('MM/DD/YYYY');
      }

      if (stopAt) {
        stopDateFormatted = stopAt.format('MM/DD/YYYY');
      }
    }

    this.state = {
      startDateFormatted,
      stopDateFormatted,
      willPlay,
    };
  }

  /**
   * Render the Tooltip when the message hasn't started or is expired
   */
  renderTooltip = () => {
    const {
      name,
      started,
      expired,
      daysOfWeek,
    } = this.props;

    const {
      willPlay,
      startDateFormatted,
      stopDateFormatted,
    } = this.state;

    const messages = [(
      <div key="message-name">
        {name}
      </div>
    )];

    if (!started) {
      messages.push(
        <div key="message-play-date">
          will begin playing on {startDateFormatted}.
        </div>,
      );
    }

    if (expired) {
      messages.push(
        <div key="message-expired">
          Expired on {stopDateFormatted}. Will be
          <br />automatically removed from all
          <br />message blocks by tomorrow.
        </div>,
      );
    }

    if (!willPlay) {
      messages.push(
        <div key="message-not-playable">
          Not playable at this location
        </div>,
      );
    }

    if (daysOfWeek.length > 0 && daysOfWeek.length < 7) {
      messages.push(
        <div key="message-not-today">
          This message is only scheduled to play on<br />
          {daysOfWeek.join(', ')}
        </div>,
      );
    }

    return (
      <div
        style={{
          textAlign: 'center',
          lineHeight: 1.67,
        }}
      >
        {messages}
      </div>
    );
  };

  render() {
    const {
      className,
      message,
      started,
      expired,
      daysOfWeek,
      day,
      showDisplayPlayer,
    } = this.props;

    const {
      willPlay,
    } = this.state;

    // message is scheduled for specific days of the week (if all days are selected treat as not scheduled)
    const scheduled = daysOfWeek.length > 0 && daysOfWeek.length < 7;

    const playControlColor = expired
      // message is expired
      ? color.error
      : color.success;

    const dayIndex = getDayOfWeekIndex(day);
    const daysOfWeekIndexes = daysOfWeek.map(day => getDayOfWeekIndex(day));

    return (
      <div className={classNames('MessageListItem', className, {
        'not-started': !started,
        expired,
        scheduled,
        'not-today': scheduled && day && !daysOfWeekIndexes.includes(dayIndex),
        'not-playable': !willPlay,
      })}
      >
        {message.mediaFormat === 'visual'
          ? (
            <VibeIcon
              className="play-message"
              icon={viPlayCircleOutline}
              color={playControlColor}
              hoverColor={playControlColor}
              tooltip="Preview Message"
              tooltipProps={{
                placement: 'top',
              }}
              disabled={message.media.length === 0}
              onClick={() => showDisplayPlayer([message])}
            />
          ) : (
            <PlayControl
              className="play-message"
              style={{
                marginRight: 0,
              }}
              id={message._id}
              name={message.name}
              src={message.media[0]?.url ?? ''}
              durationSeconds={message.media[0]?.durationSeconds ?? 0}
              color={playControlColor}
              playText="Play Message"
              pauseText="Pause Message"
            />
          )}

        <VibeTooltip
          title={this.renderTooltip()}
          placement="top"
          enterDelay={500}
        >
          <div
            style={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              overflow: 'hidden',
            }}
          >
            <div className="message-name">
              {message.name}
            </div>

            {daysOfWeek.length > 0 && daysOfWeek.length < 7 && (
              <VibeIcon
                className="calendar-icon"
                icon={viCalendar}
                color={color.primary}
                size={16}
              />
            )}
          </div>
        </VibeTooltip>
      </div>
    );
  }
}

MessageListItem.propTypes = {
  className: PropTypes.string,
  /** Message ID */
  message: PropTypes.object.isRequired,
  /** Message has Started */
  started: PropTypes.bool,
  /** Message has Expired */
  expired: PropTypes.bool,
  /** Message Start Date */
  startDate: PropTypes.string,
  /** Message End Date */
  stopDate: PropTypes.string,
  /** Message Locations */
  locations: PropTypes.arrayOf(PropTypes.shape({
    companyId: PropTypes.string,
    locationId: PropTypes.string,
  })),
  /** Company ID of Message Block */
  companyId: PropTypes.string,
  /** Location ID of Message Block */
  locationId: PropTypes.string,
  /** Days of the Week */
  daysOfWeek: PropTypes.arrayOf(PropTypes.string),
  /** Day of the week the message block is being shown */
  day: PropTypes.string,
  showDisplayPlayer: PropTypes.func,
};

MessageListItem.defaultProps = {
  className: '',
  started: true,
  expired: false,
  startDate: null,
  stopDate: null,
  locations: [],
  companyId: null,
  locationId: null,
  daysOfWeek: [],
  day: null,
  showDisplayPlayer: () => {},
};

export default MessageListItem;
